import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import { useColorScheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import LaunchIcon from "@mui/icons-material/LaunchOutlined";

import {
  Disclose,
  Loading,
  Refusal,
  Tooltip,
  controlGet,
  signOut,
} from "@lopie/kernel-ui";

import ProductPage from "./ProductPage.jsx";
import { withoutSandboxes } from "./sandbox.js";

import RemoteMount from "../components/RemoteMount";

/**
 * The console as a portal (LO-31).
 *
 * `GET /instances` is the kernel answering a question it already had the data
 * for: `CLIENT#<client_id> → app_type` says which grant governs a client, and
 * LO-31 put the remote's entry-module URL on the same row. So the kernel knows
 * which instances exist and what each may do.
 *
 * ## What this screen must not imply
 *
 * **That it knows whether an instance is working.** Provisioning status is the
 * control plane's, with its own source of truth; the kernel answers identity
 * and permission. So there are no health dots here, no "running" chips and no
 * green ticks — the only honest test of whether an instance loads is loading
 * it, which is what the Open button does.
 *
 * **That a mounted app inherits the console.** It does not, and the strip above
 * a mounted app says so, because it is the least obvious and most important
 * fact about this screen. The console passes the remote no token; the remote
 * runs its own PKCE against the same pool with its own client id, so the
 * kernel governs it by *its* manifest. Handing over the console's token would
 * hand over the console's manifest, which is the widest in the system.
 */

/**
 * One app in the register.
 *
 * One per app type rather than per identity: an app installed twice has two
 * Cognito clients and is still one app, and this screen listed the pager four
 * times and the vault five because it counted the second thing.
 *
 * The whole card opens the product page. `Open` is separate, because "show me
 * what this is" and "launch it" are different intentions and the second should
 * not be reachable only by first doing the first.
 *
 * ## Why the card is not a <button>
 *
 * It was one, with `Open` inside it stopping the click. That is a button
 * inside a button — invalid HTML, which React refuses in the dev console, and
 * an ambiguous target for anybody navigating by keyboard or screen reader:
 * one focus stop announcing the whole card, another inside it, and no way to
 * tell from the announcement which one launches the app.
 *
 * So the *title* is the button and it stretches: `::after` with `inset: 0`
 * makes its hit area the card, while the accessible name stays the product
 * name rather than the card's entire text content. `Open` is an ordinary
 * sibling sitting above that overlay, so it is one tab stop with its own
 * name and needs no `stopPropagation` to defend itself.
 *
 * The cost is that card text is no longer selectable, which is the known
 * trade of a stretched link and the right way round for a card whose whole
 * job is to be clicked.
 */
function ProductCard({ row, onShow, onMount, site }) {
  const { appType, app, entry, instances } = row;
  const title = entry?.name || app?.label || appType;
  const blurb = entry?.description || app?.description || "";
  const copies = (row.deployments || []).length;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        // The anchor for the title's stretched hit area.
        position: "relative",
        "&:hover": { borderColor: "primary.main" },
        // The card shows focus, because the thing being focused is the title
        // and its own outline would ring two words in the corner of a box
        // the whole of which is clickable.
        "&:focus-within": {
          borderColor: "primary.main",
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap", mb: 0.5 }}>
        <Typography variant="h2" sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={onShow}
            sx={{
              font: "inherit",
              color: "inherit",
              textAlign: "left",
              background: "none",
              border: 0,
              p: 0,
              cursor: "pointer",
              // The card is the hit area; the title is the name.
              "&::after": { content: '""', position: "absolute", inset: 0 },
              // Handled by the card's `:focus-within` above.
              "&:focus-visible": { outline: "none" },
            }}
          >
            {title}
          </Box>
        </Typography>
        {/* A standalone app opens its own site in a tab; a remote module is
            mounted inside this console. Both are Open to the person reading,
            and which one happens is a fact about how the app was built. */}
        {site ? (
          <Button
            size="small"
            variant="contained"
            href={site}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ position: "relative" }}
          >
            Open
          </Button>
        ) : onMount ? (
          <Button
            size="small"
            variant="contained"
            onClick={onMount}
            sx={{ position: "relative" }}
          >
            Open
          </Button>
        ) : null}
      </Stack>

      <Mono sx={{ color: "text.secondary", display: "block", mb: 1 }}>{appType}</Mono>

      {blurb ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {blurb}
        </Typography>
      ) : null}

      <Box sx={{ flexGrow: 1 }} />

      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
        {app?.agents?.length ? (
          <Chip
            size="small"
            variant="outlined"
            label={`${app.agents.length} AI ${app.agents.length === 1 ? "worker" : "workers"}`}
          />
        ) : null}
        {copies > 1 ? (
          <Chip size="small" variant="outlined" label={`${copies} installed`} />
        ) : null}
      </Stack>
    </Paper>
  );
}


function Mono({ children, sx }) {
  return (
    <Typography
      variant="body2"
      component="span"
      sx={{ fontFamily: "monospace", overflowWrap: "anywhere", ...sx }}
    >
      {children}
    </Typography>
  );
}

function Label({ children }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: "block" }}
    >
      {children}
    </Typography>
  );
}

/**
 * One provisioned instance, openable or not, and why not.
 *
 * ## It is a row in a register, not an article about one app
 *
 * This was a full-width card carrying two labelled fields, a forty-word
 * paragraph for the apps with no address, and a caption explaining why the
 * name of whoever set it up is kept. Twelve apps, twelve of those, and a
 * portal that took four screens to list what is installed.
 *
 * A register is scanned. So the card is what a person scans for — the name,
 * whether there is anything to open, and where it opens from — and the
 * explanations are on the things they explain: the paragraph about an app
 * with no address is the tooltip on "no address", where somebody who wants to
 * know why is already pointing.
 *
 * The one thing that stays at full size is the dangling alert. A broken
 * pointer is not a detail to go looking for.
 */
function InstanceCard({ instance, onOpen }) {
  const { app_type: appType, client_id: clientId, url, by, at } = instance;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h2" sx={{ overflowWrap: "anywhere" }}>
            {instance.label || appType}
          </Typography>
          {instance.label ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {appType}
            </Typography>
          ) : null}
        </Box>
        {instance.is_self ? (
          <Tooltip title="You are signed into this app. It is here because a register that omitted rows is one you cannot use to find out what exists.">
            <Chip size="small" variant="outlined" label="this console" />
          </Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {url && !instance.dangling ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<LaunchIcon />}
            onClick={() => onOpen(instance)}
          >
            Open
          </Button>
        ) : null}
      </Stack>

      {instance.dangling ? (
        <Alert severity="error" variant="outlined">
          <AlertTitle>Its permissions are missing</AlertTitle>
          {instance.detail} Nothing here can be opened, and every request this
          app makes is refused — which is about its permissions, not about
          whether the app is running.
        </Alert>
      ) : null}

      <Box sx={{ minWidth: 0 }}>
        <Label>Opens from</Label>
        {url ? (
          <Mono>{hostOf(url)}</Mono>
        ) : (
          <Tooltip
            title={
              by === "manifest"
                ? `This app is allowed to run here as ${appType}, and nothing has said where it is served from. It is the app itself rather than a copy of it installed here, which is why it has no address of its own.`
                : `This app is allowed to run here as ${appType}, and nothing has said where it is served from. Whoever installs it gives it one.`
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ borderBottom: "1px dotted", borderColor: "divider", cursor: "help", display: "inline-block" }}
            >
              no address — nothing to open
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* The client id is not on the face of the card any more.
          `5nt2lhil7nhih2r60pa25qqjhc` is the string that actually decides what
          this app may see, and it is also twenty-six characters of noise to
          everybody who is not debugging a token. It is one line down, under a
          label that says what it is for, where the person who needs it knows
          to look and nobody else has to read it. */}
      <Disclose align="row" label="Identity" sx={{ mt: -0.5 }}>
        <Stack spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Label>Signs in as</Label>
            <Mono sx={{ color: "text.secondary" }}>{clientId}</Mono>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              The Cognito app client this app's tokens are signed for.
              Permission is decided by that client and not by the name above
              it, which is why the two are worth being able to check against
              each other.
            </Typography>
          </Box>
          {url ? (
            <Box sx={{ minWidth: 0 }}>
              <Label>Full address</Label>
              <Mono sx={{ color: "text.secondary" }}>{url}</Mono>
            </Box>
          ) : null}
        </Stack>
      </Disclose>

      <Tooltip title="Deciding what an app may see is a permission decision, so who made it is kept with it.">
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: "auto",
            pt: 1,
            borderTop: 1,
            borderColor: "divider",
            cursor: "help",
          }}
        >
          set up {setUpBy(by)}
          {at ? ` on ${at.slice(0, 10)}` : ""}
        </Typography>
      </Tooltip>
    </Paper>
  );
}

/** Who set an app up, said the way a person would say it.
 *
 * The stored value is whatever wrote the pointer — `manifest` when it came
 * from the app's own declaration, `lo-provisioner` when an install wrote it.
 * Both are true and neither is a sentence: "set up by lo-provisioner" tells a
 * reader the name of a Lambda and nothing about what happened.
 *
 * Anything else is shown as it stands. A value this does not recognise is a
 * writer nobody expected, and replacing it with a friendly guess would hide
 * exactly the case worth looking at.
 */
function setUpBy(by) {
  if (!by) return "by someone unrecorded";
  if (by === "manifest") return "with the app itself";
  if (by.includes("provisioner")) return "by an install";
  return `by ${by}`;
}

/** The address, without the part only a bundler cares about.
 *
 * The full URL ends `/mf/0.1.0/remote.js` — a path that is correct, load-
 * bearing, and of no use to anybody reading this screen to find out where an
 * app lives. The host is the answer to that question; the rest is on hover for
 * whoever is actually debugging a load.
 */
function hostOf(url) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Nothing to open, and the two quite different reasons for that. */
function Empty({ instances }) {
  const none = instances.length === 0;
  return (
    <Alert severity="info" variant="outlined">
      <AlertTitle>
        {none ? "Nothing installed yet" : "Nothing here can be opened"}
      </AlertTitle>
      {none ? (
        <>
          No app has been installed into this Kernel, which is not the same
          as an install having failed. Kernel finds out an app exists when
          somebody installs it; until then there is nothing here to be wrong.
          Install one from the Library.
        </>
      ) : (
        <>
          {instances.length} {instances.length === 1 ? "app is" : "apps are"}
          {" "}allowed to run here, and none of them has an address to open.
          Those are the apps themselves rather than copies installed here.
          Listed below either way.
        </>
      )}
    </Alert>
  );
}

/** The strip above a mounted app: whose app this is, and whose permissions. */
function MountedHeader({ instance, meta, onBack }) {
  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Portal
        </Button>
        <Divider orientation="vertical" flexItem />
        <Typography variant="h2">{meta?.name || instance.app_type}</Typography>
        {meta?.version ? (
          <Chip size="small" variant="outlined" label={`v${meta.version}`} />
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Mono sx={{ color: "text.secondary" }}>{instance.client_id}</Mono>
      </Stack>

      {/*
        The least obvious and most important fact on this screen, so it is
        stated rather than assumed. It is also carefully not a claim that the
        remote succeeded in authenticating — the console cannot see that and
        the remote says so itself.
      */}
      <Alert severity="info" variant="outlined" icon={false}>
        <Typography variant="body2">
          This app is signed in <strong>as itself</strong>, not as this
          console. It does its own sign-in and this console hands it nothing —
          so what it can read is what <code>{instance.app_type}</code> asked
          for and what you are allowed to see, and not what this console can
          see. If it could borrow this console&apos;s sign-in it would inherit
          the widest reach in the system, and nothing would tell you.
        </Typography>
      </Alert>
    </Stack>
  );
}

export default function Portal({ get, token }) {
  const [instances, setInstances] = useState(null);
  const [apps, setApps] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [deployed, setDeployed] = useState([]);
  const [agents, setAgents] = useState([]);
  const [collections, setCollections] = useState({});
  const [failure, setFailure] = useState(null);
  const [open, setOpen] = useState(null);
  const [meta, setMeta] = useState(null);
  // Which app_type's product page is showing, or null for the register.
  const [showing, setShowing] = useState(null);
  const [tab, setTab] = useState(0);

  const { mode, systemMode } = useColorScheme();
  // Resolved, not raw. "system" is the default and most people never leave it,
  // and a remote told `colorScheme: "system"` would have to work out what that
  // means a second time.
  const colorScheme = (mode === "system" ? systemMode : mode) || "light";

  const load = useCallback(async () => {
    setFailure(null);
    try {
      const body = await get("/instances");
      setInstances(body.instances || []);
    } catch (e) {
      setFailure(e);
      return;
    }
    // Everything a product page needs, and all of it best effort: this screen
    // has always been able to answer "what is installed" from `/instances`
    // alone, and it should keep doing that when one of the four is
    // unreachable rather than showing a refusal for a page nobody asked for
    // yet.
    get("/apps").then((b) => setApps(b?.apps || [])).catch(() => setApps([]));
    get("/agents").then((b) => setAgents(b?.agents || [])).catch(() => setAgents([]));
    get("/schema")
      .then((b) => setCollections(Object.fromEntries(
        (b?.types || [])
          .filter((t) => t.collection)
          .map((t) => [String(t.type).toLowerCase(), t.collection]),
      )))
      .catch(() => setCollections({}));
    if (token) {
      controlGet(token)("/catalog")
        .then((b) => setCatalog(b?.catalog || []))
        .catch(() => setCatalog([]));
      // Where an app actually serves from, which the kernel's pointer does
      // not say. A standalone app's pointer carries no `url` on purpose —
      // that field is the remote module the portal mounts, and writing a
      // whole site into it would put an Open button on a module that does
      // not exist. So "can I open this" is the control plane's answer.
      controlGet(token)("/instances")
        .then((b) => setDeployed(withoutSandboxes(b?.instances)))
        .catch(() => setDeployed([]));
    }
  }, [get, token]);

  useEffect(() => {
    load();
  }, [load]);

  const onMeta = useCallback((next) => setMeta(next), []);
  const onBack = useCallback(() => {
    setOpen(null);
    setMeta(null);
  }, []);

  const mountable = useMemo(
    () => (instances || []).filter((i) => i.url && !i.dangling),
    [instances],
  );

  /**
   * One entry per app type, not per identity.
   *
   * The register used to be one card per Cognito client, which is why the
   * pager appeared four times and the vault five: an app installed twice has
   * two identities and is still one app. A person scanning this wants to know
   * what is here, and how many copies is a fact about a copy.
   */
  const byType = useMemo(() => {
    const out = new Map();
    for (const i of instances || []) {
      const key = i.app_type || i.app || "";
      if (!key) continue;
      const found = out.get(key) || { appType: key, instances: [] };
      found.instances.push(i);
      out.set(key, found);
    }
    return [...out.values()]
      .map((row) => {
        // Two kinds of openable and both count. A mountable remote has a
        // `url` on its kernel pointer; a standalone app has an `appUrl` on
        // its control-plane row and deliberately no pointer url. Judging by
        // the pointer alone put `app-vendor-spend` under Services while it
        // was serving a page at spend-by-vendor.apps.lopie.one.
        const rows = deployed.filter((d) => d.app === row.appType);
        const sites = rows
          .filter((d) => d.appUrl && d.desiredState !== "absent")
          .map((d) => ({ url: d.appUrl, name: d.displayName || d.name }));
        return {
          ...row,
          app: apps.find((a) => (a.app_type || a.app) === row.appType) || null,
          entry: catalog.find((c) => c.app === row.appType) || null,
          // Installs, not identities. An app installed once has two Cognito
          // clients — a browser one and a machine one — and counting those
          // said "2 installed" about one deployment.
          deployments: rows.filter((d) => d.desiredState !== "absent"),
          sites,
          openable: row.instances.some((i) => i.url) || sites.length > 0,
        };
      })
      .sort((a, b) => a.appType.localeCompare(b.appType));
  // `deployed` belongs here and was missing. Without it the memo keeps the
  // value it computed before the control plane answered, so whether an
  // app counted as openable depended on which fetch won — it read Apps (4)
  // on one load and Apps (2) on the next, from the same data.
  }, [instances, apps, catalog, deployed]);

  /**
   * An installable product with nothing installed is not on this screen.
   *
   * Vault and CrowCase were listed as Services, and neither is one. Vault is a
   * product somebody installs from the Library — it has a catalog row, a
   * composition and a version — and it currently has no deployment at all, so
   * listing it here answered "what is provisioned here" with something that is
   * not. CrowCase is a product that *consumes* this kernel from outside and is
   * never deployed into it.
   *
   * What made them appear is that both hold a Cognito client created by this
   * environment's Terraform, from before apps were installed rather than
   * terraformed. A client is an identity, not a deployment, and this register
   * had been treating the first as evidence of the second.
   *
   * So: an app the Library can install is shown only when something is
   * actually installed. The rest — the kernel, the console, the assistant, the
   * agents — are this environment's own stack, they run, and they are
   * Services.
   *
   * Deliberately not a list of names in this file. The catalog is the thing
   * that says "this is installable", it is read at render time, and an app
   * added to it tomorrow gets this behaviour without anybody editing a
   * screen.
   */
  const provisioned = byType.filter(
    (r) => !r.entry || r.deployments.length > 0,
  );

  // Apps have somewhere to open. Services are the rest — the kernel itself,
  // the assistant, the connectors, the agents' own app types. They are not
  // broken and they are not waiting for anything; there is simply no page,
  // and twelve cards saying "nothing to open" on a screen asking whether you
  // can open it was the whole complaint.
  const openable = provisioned.filter((r) => r.openable);
  const services = provisioned.filter((r) => !r.openable);
  const shown = showing ? byType.find((r) => r.appType === showing) : null;

  if (open) {
    return (
      <Stack spacing={2}>
        <MountedHeader instance={open} meta={meta} onBack={onBack} />
        <RemoteMount
          // Keyed by the instance, so opening a different one is a fresh mount
          // rather than a re-render of the same host element.
          key={open.client_id}
          instance={open}
          colorScheme={colorScheme}
          signOut={signOut}
          onMeta={onMeta}
        />
      </Stack>
    );
  }

  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }

  if (!instances) return <Loading label="Reading the instances" />;

  if (shown) {
    return (
      <ProductPage
        app={shown.app}
        entry={shown.entry}
        instances={shown.instances}
        agents={agents}
        collections={collections}
        sites={shown.sites}
        deployments={shown.deployments}
        onBack={() => setShowing(null)}
      />
    );
  }

  return (
    <Stack spacing={2}>
      {/* The sentence that has to survive being the only one read is the
          second one, so it is the summary. Everything else about how an
          instance gets its identity is true, is worth having, and is not what
          somebody scanning a list of apps came for. */}
      <Disclose
        summary="Nothing here says an app is healthy, running, or finished installing."
        label="What this register is"
      >
        <Typography variant="body2" color="text.secondary">
          Every app installed into this Kernel, each with its own sign-in and
          its own address to open from — both set when it was installed, and
          neither something an app can claim for itself. Kernel knows who an
          app is and what it may see; whether it loads is found out by opening
          it. What an app can read is what it asked for and what you are
          allowed to see, never what this console can see —{" "}
          <Link href="#apps" underline="hover">
            the Apps screen
          </Link>{" "}
          shows every ceiling side by side.
        </Typography>
      </Disclose>

      {mountable.length === 0 ? <Empty instances={instances} /> : null}

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={`Apps (${openable.length})`} />
        <Tab label={`Services (${services.length})`} />
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
        {tab === 0
          ? "Things with a page somebody opens. One card per app, however many copies of it are installed."
          : "What runs underneath: the kernel itself, the build assistant, the connectors and the apps that exist only to hold an agent. None of them has a page to open, and none of them is missing one."}
      </Typography>

      {/* A grid, because this is a register and a column of full-width cards
          is four screens of scrolling to answer "what is installed". */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fit, minmax(300px, 1fr))",
          },
          alignItems: "stretch",
        }}
      >
        {(tab === 0 ? openable : services).map((row) => (
          <ProductCard
            key={row.appType}
            row={row}
            onShow={() => setShowing(row.appType)}
            site={row.sites?.[0]?.url || null}
            onMount={
              row.instances.find((i) => i.url && !i.dangling)
                ? () => setOpen(row.instances.find((i) => i.url && !i.dangling))
                : null
            }
          />
        ))}
      </Box>

      {(tab === 0 ? openable : services).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {tab === 0
            ? "Nothing here opens a page yet."
            : "Nothing runs underneath that is not already an app."}
        </Typography>
      ) : null}

    </Stack>
  );
}
